import { useState, useEffect } from "react";
import { X, Settings, Check } from "lucide-react";

const CONSENT_KEY = "skiflow_cookie_consent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: true, marketing: true });

  useEffect(() => {
    const saved = localStorage.getItem(CONSENT_KEY);
    if (!saved) {
      const t = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const applyGtag = (analytics, marketing) => {
    if (window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: analytics ? "granted" : "denied",
        ad_storage: marketing ? "granted" : "denied",
      });
    }
  };

  const acceptAll = () => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ accepted: true, analytics: true, marketing: true, date: new Date().toISOString() }));
    applyGtag(true, true);
    setVisible(false);
  };

  const savePrefs = () => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ accepted: true, analytics: prefs.analytics, marketing: prefs.marketing, date: new Date().toISOString() }));
    applyGtag(prefs.analytics, prefs.marketing);
    setVisible(false);
  };

  const rejectAll = () => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ accepted: false, analytics: false, marketing: false, date: new Date().toISOString() }));
    applyGtag(false, false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9998] px-4 pb-4 sm:pb-6">
      <div
        className="max-w-4xl mx-auto rounded-2xl shadow-2xl overflow-hidden"
        style={{ border: "1px solid rgba(124,58,237,0.2)", background: "#fff" }}
      >
        {/* Barra degradado superior */}
        <div className="h-1.5" style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #ec4899 100%)" }} />

        <div className="p-5 sm:p-6">
          {/* Texto principal */}
          <div className="flex gap-3 items-start mb-4">
            <div
              className="hidden sm:flex h-10 w-10 shrink-0 rounded-xl items-center justify-center text-lg"
              style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%)" }}
            >
              🍪
            </div>
            <div className="flex-1">
              <p className="font-bold text-slate-900 text-sm sm:text-base mb-1">Utilizamos cookies</p>
              <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">
                Utilizamos cookies propias y de terceros para mejorar nuestros servicios, analizar el tráfico y personalizar tu experiencia.
                Al hacer clic en <strong>"Aceptar todas"</strong>, aceptas su uso. Puedes configurar o rechazar las cookies haciendo clic en <strong>"Configurar"</strong>.{" "}
                <a href="/politica-cookies" className="text-purple-600 hover:underline font-medium">Política de cookies</a>
                {" · "}
                <a href="/politica-privacidad" className="text-purple-600 hover:underline font-medium">Privacidad</a>
              </p>
            </div>
          </div>

          {/* Panel de configuración expandible */}
          {showConfig && (
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Necesarias */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="h-5 w-9 rounded-full bg-green-500 relative flex-shrink-0 cursor-not-allowed">
                  <div className="absolute right-0.5 top-0.5 h-4 w-4 bg-white rounded-full shadow" />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-800">Necesarias</p>
                  <p className="text-xs text-slate-600">Siempre activas</p>
                </div>
              </div>
              {/* Analíticas */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl cursor-pointer" onClick={() => setPrefs(p => ({ ...p, analytics: !p.analytics }))}>
                <button
                  type="button"
                  className={`h-5 w-9 rounded-full relative flex-shrink-0 transition-colors ${prefs.analytics ? "bg-purple-600" : "bg-slate-300"}`}
                >
                  <div className={`absolute top-0.5 h-4 w-4 bg-white rounded-full shadow transition-all ${prefs.analytics ? "right-0.5" : "left-0.5"}`} />
                </button>
                <div>
                  <p className="text-xs font-bold text-slate-800">Analíticas</p>
                  <p className="text-xs text-slate-600">Google Analytics</p>
                </div>
              </div>
              {/* Marketing */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl cursor-pointer" onClick={() => setPrefs(p => ({ ...p, marketing: !p.marketing }))}>
                <button
                  type="button"
                  className={`h-5 w-9 rounded-full relative flex-shrink-0 transition-colors ${prefs.marketing ? "bg-purple-600" : "bg-slate-300"}`}
                >
                  <div className={`absolute top-0.5 h-4 w-4 bg-white rounded-full shadow transition-all ${prefs.marketing ? "right-0.5" : "left-0.5"}`} />
                </button>
                <div>
                  <p className="text-xs font-bold text-slate-800">Marketing</p>
                  <p className="text-xs text-slate-600">Ads, Remarketing</p>
                </div>
              </div>
            </div>
          )}

          {/* Botones */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={acceptAll}
              className="px-5 py-2.5 text-sm font-bold text-white rounded-xl transition-all hover:opacity-90 active:scale-95"
              style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #ec4899 100%)" }}
            >
              ✓ Aceptar todas
            </button>

            {showConfig ? (
              <button
                onClick={savePrefs}
                className="px-5 py-2.5 text-sm font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors"
              >
                <Check className="inline h-4 w-4 mr-1" />Guardar preferencias
              </button>
            ) : (
              <button
                onClick={() => setShowConfig(true)}
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
              >
                <Settings className="h-4 w-4" />Configurar
              </button>
            )}

            <button
              onClick={rejectAll}
              className="ml-auto text-xs text-slate-600 hover:text-slate-600 transition-colors underline underline-offset-2"
            >
              Rechazar no esenciales
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
